import { useState } from 'react';
import { ShieldCheck, TriangleAlert } from 'lucide-react';

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await onSubmit(password);
    } catch {
      setError('Failed to set up encryption. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="applock-screen" data-testid="applock-setup">
      <div className="applock-card">
        <div className="applock-icon-wrap">
          <ShieldCheck size={36} strokeWidth={2} />
        </div>
        <h1 className="applock-title">Create Password</h1>
        <p className="applock-description">
          Choose a password to encrypt all stored patient data. This
          password will be required each time you open the app.
        </p>

        <div className="applock-warning">
          <TriangleAlert size={18} strokeWidth={2} />
          <span>
            <strong>No password recovery.</strong> If you forget this
            password, encrypted data is permanently inaccessible.
          </span>
        </div>

        <form className="applock-form" onSubmit={handleSubmit}>
          <div className="applock-field">
            <label htmlFor="applock-setup-password">Password</label>
            <input
              id="applock-setup-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>
          <div className="applock-field">
            <label htmlFor="applock-setup-confirm">Confirm password</label>
            <input
              id="applock-setup-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </div>

          {error && <p className="applock-error">{error}</p>}

          <div className="applock-actions">
            <button
              type="submit"
              className="applock-btn applock-btn-primary"
              disabled={busy || !password || !confirm}
            >
              {busy ? 'Encrypting...' : 'Create Password'}
            </button>
            <button
              type="button"
              className="applock-btn applock-btn-secondary"
              onClick={onSkip}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
